import { z } from 'zod';
import { CAMPAIGN_LIMITS, CAMPAIGN_ROLES, isCalendarDate } from '../campaigns.js';
import { OCCASION_LIMITS, OCCASION_TRIGGER_KINDS } from './occasions.js';
import { THEME_SLOT_LIMITS } from './theme-slots.js';
import { YEAR_PLAN_LIMITS } from './year-plan.js';
/**
 * The planning conversation (spec 110 section 4.10, E300 S300-3,
 * REQ-KAMP-018..026): a guided dialog whose state machine and question
 * catalogue the server owns. The `campaign_planner` job only proposes a plan
 * and picks the catalogue questions it still needs; a human applies the plan.
 */
export const PLANNING_SESSION_KINDS = ['campaign', 'year'];
export const PLANNING_SESSION_STATUSES = ['proposing', 'asking', 'plan_ready', 'refining', 'applied', 'abandoned'];
/** Statuses in which a run of the session is in flight. */
export const PLANNING_RUNNING_STATUSES = ['proposing', 'refining'];
/** Statuses that end a session; a property may open a new one of the same kind afterwards. */
export const PLANNING_CLOSED_STATUSES = ['applied', 'abandoned'];
/** The steps of one session, each one `campaign_planner` run. */
export const PLANNING_STEPS = ['propose', 'plan', 'refine'];
/**
 * The question catalogue (spec 110 section 4.10): the model picks only from
 * these keys, never one twice; the web words them through i18n only as a
 * fallback, the model words them in the tenant language.
 */
export const PLANNING_QUESTION_KEYS = ['goal', 'gaps', 'special', 'cta', 'photos'];
export const PLANNING_LIMITS = {
    maxQuestions: 6,
    maxProposedQuestions: 5,
    minChips: 2,
    maxChips: 5,
    chipLabelMaxLength: 40,
    chipValueMaxLength: 40,
    questionTextMaxLength: 300,
    answerTextMaxLength: 500,
    instructionMaxLength: 500,
    maxRefinements: 5,
    /** propose + plan + five refinements. */
    maxRuns: 7,
    summaryMaxLength: 500,
    nameMaxLength: CAMPAIGN_LIMITS.nameMaxLength,
    goalMaxLength: 300,
    titleMaxLength: 120,
    descriptionMaxLength: 600,
    photoHintMaxLength: 200,
    factTextMaxLength: 300,
    minFactIds: 1,
    maxFactIds: 10,
    maxFacts: 10,
    maxMediaPerPost: 5,
    minPosts: 4,
    maxPosts: 9,
    minMoments: 2,
    maxMoments: 4,
    maxOccasions: 4,
    maxPhotoTasks: 8,
    photoTaskTextMaxLength: 200,
    keyMaxLength: 40,
    maxPillars: 20,
    /** As built: a campaign spans at most a quarter. */
    maxCampaignDays: 92,
    /** A session without activity for this many days is abandoned by the weekly routine. */
    idleDays: 14,
    /** A pillar with at least this many usable photos needs no `photos` question. */
    enoughPhotos: 3,
    /** The year plan (spec 110 section 4.11, S300-4): caps per plan. */
    maxYearCampaigns: 12,
    maxYearSlots: YEAR_PLAN_LIMITS.maxSlots,
    maxYearOccasions: 12,
    /** A year plan without a chosen period covers today .. today + 364 tenant days. */
    yearWindowDays: 364,
    /** A chosen year period spans at most this many days. */
    maxYearWindowDays: YEAR_PLAN_LIMITS.maxPeriodDays,
    maxSlotDays: YEAR_PLAN_LIMITS.maxSlotDays,
};
const calendarDate = z.string().refine(isCalendarDate, { message: 'expected a calendar day YYYY-MM-DD' });
const key = z.string().trim().min(1).max(PLANNING_LIMITS.keyMaxLength);
export const planningChipSchema = z
    .object({
    value: z.string().trim().min(1).max(PLANNING_LIMITS.chipValueMaxLength),
    label: z.string().trim().min(1).max(PLANNING_LIMITS.chipLabelMaxLength),
})
    .strict();
/** A question as the model asks it: a catalogue key, its wording and 2..5 chips. */
export const planningQuestionSchema = z
    .object({
    key: z.enum(PLANNING_QUESTION_KEYS),
    text: z.string().trim().min(1).max(PLANNING_LIMITS.questionTextMaxLength),
    chips: z.array(planningChipSchema).min(PLANNING_LIMITS.minChips).max(PLANNING_LIMITS.maxChips),
})
    .strict();
export const planFactSchema = z
    .object({
    text: z.string().trim().min(1).max(PLANNING_LIMITS.factTextMaxLength),
    knowledge_item_ids: z.array(z.uuid()).min(PLANNING_LIMITS.minFactIds).max(PLANNING_LIMITS.maxFactIds),
})
    .strict();
export const planPostSchema = z
    .object({
    key,
    role: z.enum(CAMPAIGN_ROLES),
    title: z.string().trim().min(1).max(PLANNING_LIMITS.titleMaxLength),
    description: z.string().trim().max(PLANNING_LIMITS.descriptionMaxLength),
    pillar_key: z.string().trim().min(1).max(64),
    date: calendarDate,
    photo_hint: z.string().trim().min(1).max(PLANNING_LIMITS.photoHintMaxLength).nullable(),
    media_asset_ids: z.array(z.uuid()).max(PLANNING_LIMITS.maxMediaPerPost),
    facts: z.array(planFactSchema).max(PLANNING_LIMITS.maxFacts),
})
    .strict();
/** An occasion post: waits on standby for its trigger and never carries a date. */
export const planOccasionSchema = z
    .object({
    key,
    title: z.string().trim().min(1).max(Math.min(PLANNING_LIMITS.titleMaxLength, OCCASION_LIMITS.labelMaxLength)),
    description: z.string().trim().max(PLANNING_LIMITS.descriptionMaxLength),
    pillar_key: z.string().trim().min(1).max(64),
    trigger_kind: z.enum(OCCASION_TRIGGER_KINDS),
    label_match: z.string().trim().min(1).max(OCCASION_LIMITS.labelMaxLength).nullable(),
    own_date: calendarDate.nullable(),
    photo_hint: z.string().trim().min(1).max(PLANNING_LIMITS.photoHintMaxLength).nullable(),
})
    .strict();
export const planPhotoTaskSchema = z
    .object({
    key,
    text: z.string().trim().min(1).max(PLANNING_LIMITS.photoTaskTextMaxLength),
    post_keys: z.array(key).max(PLANNING_LIMITS.maxPosts + PLANNING_LIMITS.maxYearOccasions),
})
    .strict();
export const planRoleTargetsSchema = z
    .object({
    opening: z.literal(1),
    moment: z.number().int().min(PLANNING_LIMITS.minMoments).max(PLANNING_LIMITS.maxMoments),
    closing: z.literal(1),
})
    .strict();
export const planCampaignSchema = z
    .object({
    name: z.string().trim().min(1).max(PLANNING_LIMITS.nameMaxLength),
    goal: z.string().trim().min(1).max(PLANNING_LIMITS.goalMaxLength),
    period_start: calendarDate,
    period_end: calendarDate,
    pillar_keys: z.array(z.string().trim().min(1).max(64)).min(1).max(PLANNING_LIMITS.maxPillars),
    role_targets: planRoleTargetsSchema,
})
    .strict();
/** The plan of a `campaign` session, schema version 1 (spec 110 section 4.10). */
export const campaignPlanSchema = z
    .object({
    summary: z.string().trim().max(PLANNING_LIMITS.summaryMaxLength),
    campaign: planCampaignSchema,
    posts: z.array(planPostSchema).min(PLANNING_LIMITS.minPosts).max(PLANNING_LIMITS.maxPosts),
    occasions: z.array(planOccasionSchema).max(PLANNING_LIMITS.maxOccasions),
    photo_tasks: z.array(planPhotoTaskSchema).max(PLANNING_LIMITS.maxPhotoTasks),
})
    .strict();
/**
 * A campaign of a year plan (spec 110 section 4.11): as in a campaign plan,
 * with a key the slots refer to and dated posts only inside the near-term
 * window; the weekly planner prepares the later posts by the role targets.
 */
export const yearPlanCampaignEntrySchema = planCampaignSchema
    .extend({
    key,
    posts: z.array(planPostSchema).max(PLANNING_LIMITS.maxPosts),
})
    .strict();
/** A theme slot for a period without a campaign goal: running posts, prepared just in time. */
export const planSlotSchema = z
    .object({
    key,
    theme: z.string().trim().min(1).max(THEME_SLOT_LIMITS.themeMaxLength),
    pillar_key: z.string().trim().min(1).max(64),
    period_start: calendarDate,
    period_end: calendarDate,
    campaign_key: key.nullable(),
})
    .strict();
/** The plan of a `year` session, schema version 1 (spec 110 section 4.11). */
export const yearPlanSchema = z
    .object({
    summary: z.string().trim().max(PLANNING_LIMITS.summaryMaxLength),
    campaigns: z.array(yearPlanCampaignEntrySchema).max(PLANNING_LIMITS.maxYearCampaigns),
    slots: z.array(planSlotSchema).max(PLANNING_LIMITS.maxYearSlots),
    occasions: z.array(planOccasionSchema).max(PLANNING_LIMITS.maxYearOccasions),
    photo_tasks: z.array(planPhotoTaskSchema).max(PLANNING_LIMITS.maxPhotoTasks),
})
    .strict();
/** Either plan; the session's kind decides which one is valid. */
export const planningPlanSchema = z.union([campaignPlanSchema, yearPlanSchema]);
export const isYearPlan = (plan) => 'campaigns' in plan;
/** Every dated post of a plan, in plan order. */
export const planPosts = (plan) => (isYearPlan(plan) ? plan.campaigns.flatMap((campaign) => campaign.posts) : plan.posts);
/** The plan schema of a session kind. */
export const planSchemaOf = (kind) => (kind === 'year' ? yearPlanSchema : campaignPlanSchema);
/** The run's parameters (subject `property`). */
export const campaignPlannerParamsSchema = z
    .object({
    session_id: z.uuid(),
    property_id: z.uuid(),
    step: z.enum(PLANNING_STEPS),
    instruction: z.string().trim().min(1).max(PLANNING_LIMITS.instructionMaxLength).optional(),
})
    .strict();
/** The model's final answer: the questions it still needs (propose only), the plan and a summary of what it did. */
export const campaignPlannerOutputSchema = z
    .object({
    questions: z.array(planningQuestionSchema).max(PLANNING_LIMITS.maxProposedQuestions),
    plan: planningPlanSchema.nullable(),
    summary: z.string().trim().max(PLANNING_LIMITS.summaryMaxLength),
})
    .strict();
//# sourceMappingURL=planning-sessions.js.map